// TrackingConvergence — pure signal computation for the coaching HUD.
//
// WebXR doesn't expose a "tracking quality %" API. What we can measure
// per frame are three proxy signals:
//
//   1. elapsedMs since session start (warm-up phase timing)
//   2. viewerPoseFrames — count of frames that returned a valid
//      XRViewerPose. Non-zero means SLAM has established basic
//      motion tracking.
//   3. hit-test attempts split into empty vs with-result — how often
//      the camera-forward ray is intersecting an actually-detected
//      real-world plane.
//
// This module maps those into a 0..1 "convergence" score plus a
// user-facing hint ("Warming up", "Move slowly", "Point at a textured
// surface") so the coaching overlay tells the user what to do next.
//
// Progress model (each stage adds up to 1.0 total):
//   0.20 : session initialised (elapsedMs ramps 0..1.5s)
//   0.30 : SLAM tracking (viewer poses flowing)
//   0.30 : hit-test source active AND running attempts
//   0.20 : hit-test returning results (any successful frames)
//
// Placement `ready()` still fires only on the first actual hit — see
// WebXRDriver.onFrame. Convergence is purely diagnostic.

export interface TrackingSignals {
  /** Milliseconds since the session started (renderer stamps this
   *  from performance.now() the first frame). */
  readonly elapsedMs: number
  /** Cumulative frames where frame.getViewerPose returned non-null.
   *  Non-zero means SLAM is up. */
  readonly viewerPoseFrames: number
  /** Cumulative frames where getHitTestResults returned ≥ 1 result. */
  readonly hitTestFramesWithResult: number
  /** Cumulative frames where getHitTestResults returned an empty list. */
  readonly hitTestFramesEmpty: number
}

export interface TrackingConvergence {
  readonly progress: number
  readonly hint: string
  readonly placeAnywayUnlocked: boolean
}

/** After this many milliseconds without a hit, we unlock a "Place
 *  anyway" fallback in the coaching overlay so the user isn't stuck
 *  in poor tracking conditions (dark room, plain wall, glare). */
export const PLACE_ANYWAY_UNLOCK_MS = 12_000

/** Elapsed time (ms) at which the session-init phase is considered
 *  complete. Ramps 0..0.20 progress across this window. */
const INIT_RAMP_END_MS = 1500

/** Viewer-pose frame count that fully saturates the SLAM-tracking
 *  phase. ~1 second at 60 fps is enough to say "tracking is up". */
const SLAM_SATURATION_FRAMES = 60

/** Hit-test attempt count that saturates the "attempts running" phase. */
const HIT_ATTEMPT_SATURATION = 90

/** Successful hit-test frames that saturate the last phase. */
const HIT_SUCCESS_SATURATION = 5

export function computeTrackingConvergence(
  s: TrackingSignals,
): TrackingConvergence {
  const initPhase = 0.2 * clamp01(s.elapsedMs / INIT_RAMP_END_MS)
  const slamPhase = 0.3 * clamp01(s.viewerPoseFrames / SLAM_SATURATION_FRAMES)
  const hitAttempts = s.hitTestFramesEmpty + s.hitTestFramesWithResult
  const attemptPhase = 0.3 * clamp01(hitAttempts / HIT_ATTEMPT_SATURATION)
  const successPhase =
    0.2 * clamp01(s.hitTestFramesWithResult / HIT_SUCCESS_SATURATION)
  const progress = clamp01(initPhase + slamPhase + attemptPhase + successPhase)

  const hint = pickHint(s, progress)
  const placeAnywayUnlocked = s.elapsedMs >= PLACE_ANYWAY_UNLOCK_MS

  return { progress, hint, placeAnywayUnlocked }
}

/**
 * Every pre-hit hint asks for MOTION, and names the axis once tracking
 * is up.
 *
 * Depth-from-motion triangulates across frames captured from different
 * camera POSITIONS. Rotation alone — panning the phone while standing
 * still — moves the view without moving the optical centre, so it
 * yields no new parallax and no depth. Translation toward and away from
 * the target is what actually converges the scan.
 *
 * The old copy ("Move your phone slowly") didn't distinguish the two,
 * and the 2026-08-22 field test stalled on exactly that ambiguity: the
 * tester held the phone still, waiting for the surface to be found.
 */
function pickHint(s: TrackingSignals, progress: number): string {
  if (progress < 0.15) return 'Warming up the camera — start moving your phone…'
  if (s.viewerPoseFrames < 20) {
    return 'Move your phone toward and away from your car to scan.'
  }
  if (s.hitTestFramesWithResult === 0) {
    return 'Keep moving closer and further — aim where your tire meets the ground.'
  }
  return 'Almost there — hold steady.'
}

function clamp01(v: number): number {
  if (v < 0) return 0
  if (v > 1) return 1
  return v
}

// ── Tracking health ──────────────────────────────────────────────────
//
// A different question from convergence above, which is a coaching
// progress ramp and saturates once hit-tests return results.
//
// That saturation is the trap. Melih's Xiaomi (2026-08-24) returned
// hit-test results for the whole session — `FLOOR`, `normal.y 1.000` —
// while ARCore had detected ZERO planes and the depth map reported the
// floor 3m further away than it was, 19 usable samples in 289 attempts.
// Convergence read as complete while the device could not map anything,
// so everything placed drifted out of view. The user was shown a
// confident UI right up to the point where it fell apart.
//
// The discriminator is the plane count. Plane detection and
// depth-from-motion are both downstream of ARCore's feature-point map,
// so a device finding no planes after a fair chance is not tracking,
// whatever its hit-test says. Chrome's hit-test falls back to an
// estimated-surface path when no planes exist, which is why it looks
// healthy and is not evidence.

export type TrackingHealth = 'converging' | 'healthy' | 'degraded' | 'unknown'

/** How long a device gets before zero planes counts against it.
 *
 *  ARCore needs seconds of genuine parallax before its first plane, and
 *  a user reading the coaching copy before moving is normal. Melih's
 *  session had made 289 depth attempts — roughly 14s at 60fps — before
 *  the screenshot, so this sits just past that: long enough not to fire
 *  on a healthy session, short enough to catch a real one. */
export const TRACKING_GRACE_MS = 15_000

/** Depth yield below which the depth stream is considered unusable.
 *  Corroboration carried on the telemetry, NOT a gate — plenty of
 *  ARCore devices have no usable Depth API and map scenes perfectly. */
export const DEGRADED_DEPTH_YIELD = 0.1

export interface TrackingHealthSignals {
  readonly elapsedMs: number
  /** Was 'plane-detection' in session.enabledFeatures? Without it a
   *  zero plane count means "we cannot see", not "the device is
   *  broken". */
  readonly planeDetectionGranted: boolean
  readonly planeCount: number
  readonly depthGranted: boolean
  readonly depthFramesWith: number
  readonly depthFramesWithout: number
}

/** Fraction of depth attempts that produced a usable sample, or null
 *  when nothing has been attempted. */
export function depthYield(s: {
  depthFramesWith: number
  depthFramesWithout: number
}): number | null {
  const attempts = s.depthFramesWith + s.depthFramesWithout
  if (attempts <= 0) return null
  return s.depthFramesWith / attempts
}

/**
 * Can this device map the scene?
 *
 * Deliberately conservative about `degraded`: it is the verdict that
 * reaches an operator's Slack channel, and a false alarm there costs
 * more than a missed one. Anything we cannot judge returns `unknown`.
 */
export function deriveTrackingHealth(s: TrackingHealthSignals): TrackingHealth {
  if (!s.planeDetectionGranted) return 'unknown'
  if (s.planeCount > 0) return 'healthy'
  if (s.elapsedMs < TRACKING_GRACE_MS) return 'converging'
  return 'degraded'
}
